/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getEnvVarOptional, COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";

/** A single curated link in an `llms.txt` section. */
export interface LlmsLink {
	/** Human-readable link label. */
	title: string;
	/** Absolute or root-relative URL. Root-relative values are prefixed with the resolved base URL. */
	url: string;
	/** Optional short description shown after the link. */
	notes?: string;
}

/** A titled group of links rendered as a `## Heading` section. */
export interface LlmsSection {
	title: string;
	links: LlmsLink[];
	/**
	 * Mark this group as the convention's skippable section — agents working with a small
	 * context budget drop it first. Optional sections are merged into a single `## Optional`
	 * heading emitted last, after the sitemap, regardless of where the caller places them.
	 * Use it for editorial, archival, and boilerplate links.
	 */
	optional?: boolean;
}

/** Heading the llms.txt convention reserves for skippable links. */
const OPTIONAL_SECTION_TITLE = "Optional";

/** Input for {@link renderLlmsTxt}. */
export interface LlmsConfig {
	/** Site/product name — rendered as the top-level `# Heading`. */
	title: string;
	/** One-line summary — rendered as the leading `>` blockquote. */
	summary: string;
	/**
	 * Free-form paragraphs rendered between the summary blockquote and the first section.
	 * The llms.txt convention reserves this slot for the facts an agent needs before it
	 * follows any link — what the product is, who publishes it, how it is licensed, what it
	 * is commonly confused with. Each entry becomes its own paragraph.
	 *
	 * Inline Markdown is fine, but do not include headings: a `##` here would be read as a
	 * section boundary. Whitespace is collapsed so an entry can be wrapped in source.
	 */
	details?: string | string[];
	/**
	 * Canonical site origin (may include a base path, e.g. `https://www.fab.com/o`).
	 * Defaults to `EPIC_SERVER_URL` + `COSMOS_SITE_BASE_PATH` — the same join used by
	 * the robots/sitemap helpers. Used to resolve root-relative link URLs and the sitemap link.
	 */
	baseUrl?: string;
	/** Curated link groups. Groups with no links are skipped. */
	sections?: LlmsSection[];
	/** Append a `## Sitemaps` section linking `${baseUrl}/sitemap.xml`. Defaults to `true`. */
	includeSitemap?: boolean;
}

// Lazily resolve the base URL to avoid calling env accessors at module-evaluation time
// (this module is pulled into client bundles via barrel exports, where EPIC_SERVER_URL
// is unavailable). Mirrors the approach in ./robots.ts.
function getDefaultBaseUrl(): string {
	return `${getEnvVarOptional("EPIC_SERVER_URL") ?? ""}${COSMOS_SITE_BASE_PATH()}`;
}

function stripTrailingSlash(value: string): string {
	return value.endsWith("/") ? value.slice(0, -1) : value;
}

/**
 * Collapse newlines and runs of whitespace so a value wrapped across source lines still
 * renders as one paragraph or one list item. Without this, a newline in `details` or `notes`
 * silently breaks the document structure — a `##` on its own line becomes a real heading,
 * and a wrapped `notes` orphans its tail outside the list.
 */
function collapseWhitespace(value: string): string {
	return value.replace(/\s+/g, " ").trim();
}

/** Resolve a link URL against the base URL when it is root-relative. */
function resolveUrl(url: string, baseUrl: string): string {
	if (url.startsWith("http://") || url.startsWith("https://")) return url;
	return `${stripTrailingSlash(baseUrl)}${url.startsWith("/") ? url : `/${url}`}`;
}

function renderSection(section: LlmsSection, baseUrl: string): string {
	const links = section.links.map((link) => {
		const label = `[${collapseWhitespace(link.title)}](${resolveUrl(link.url, baseUrl)})`;
		const notes = link.notes ? collapseWhitespace(link.notes) : "";
		return notes ? `- ${label}: ${notes}` : `- ${label}`;
	});
	return [`## ${section.title}`, "", ...links].join("\n");
}

/**
 * Render an `llms.txt` document (Markdown) from a curated config.
 * Format follows the llms.txt convention: an `# H1` title, a `>` summary blockquote,
 * detail paragraphs, curated `## Section` link lists, and (by default) a `## Sitemaps` link.
 *
 * Sections marked `optional` are merged into a single `## Optional` heading and emitted
 * last, after the sitemap, wherever the caller placed them.
 *
 * @throws if the resolved base URL is empty. Every link in the document must be absolute,
 * so a missing `EPIC_SERVER_URL` has to fail loudly — otherwise the route happily serves a
 * complete-looking file whose links are bare paths that resolve against whatever host the
 * crawler fetched from.
 */
export function renderLlmsTxt(config: LlmsConfig): string {
	// `||` not `??`: an empty string is a missing base URL, not a valid one.
	const baseUrl = config.baseUrl || getDefaultBaseUrl();
	if (!baseUrl) {
		throw new Error(
			"renderLlmsTxt: no base URL. Set EPIC_SERVER_URL or pass config.baseUrl — llms.txt links must be absolute.",
		);
	}

	const provided = (config.sections ?? []).filter((section) => section.links.length > 0);
	const sections = provided.filter((section) => !section.optional);

	if (config.includeSitemap !== false) {
		sections.push({ title: "Sitemaps", links: [{ title: "Sitemap", url: "/sitemap.xml" }] });
	}

	// Merge every optional group into the single `## Optional` heading the convention expects.
	const optionalLinks = provided.filter((section) => section.optional).flatMap((section) => section.links);
	if (optionalLinks.length > 0) {
		sections.push({ title: OPTIONAL_SECTION_TITLE, links: optionalLinks });
	}

	const details = (typeof config.details === "string" ? [config.details] : (config.details ?? []))
		.map(collapseWhitespace)
		.filter(Boolean);

	const blocks = [
		`# ${config.title}`,
		`> ${collapseWhitespace(config.summary)}`,
		...details,
		...sections.map((section) => renderSection(section, baseUrl)),
	];

	// Join blocks with a blank line between each and terminate with a trailing newline.
	return `${blocks.join("\n\n")}\n`;
}

/**
 * Build a Next.js Route Handler (`GET`) that serves an `llms.txt`, mirroring the thin
 * `robots.ts` wiring. Accepts either a static config or a (sync/async) factory so
 * multi-tenant apps can resolve per-request config. A factory returning `undefined`
 * yields a 404, which is how a multi-tenant app opts a tenant out.
 *
 * Only `GET` is exported by callers, so Next.js returns `405` for other methods.
 */
export function createLlmsTxtRoute(
	config: LlmsConfig | (() => LlmsConfig | undefined | Promise<LlmsConfig | undefined>),
): () => Promise<Response> {
	return async function GET(): Promise<Response> {
		const resolved = typeof config === "function" ? await config() : config;
		if (!resolved) return new Response("Not Found", { status: 404 });

		return new Response(renderLlmsTxt(resolved), {
			headers: {
				"content-type": "text/plain; charset=utf-8",
				"cache-control": "public, max-age=3600",
			},
		});
	};
}
